/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import {
  AppShell,
  rem,
  useMantineTheme,
  Group,
  Flex,
  Burger,
  Menu,
  Text,
  Box,
  Button,
} from '@mantine/core';
import type { ButtonProps } from '@mantine/core/lib/components';
import { useDisclosure } from '@mantine/hooks';
import { isUndefined, randomInt } from 'es-toolkit';
import React, { Suspense } from 'react';
import { Link, Outlet } from 'react-router-dom';

import Paths from '@fai/friendli-suite/features/routes/paths';
import SuiteLogo from '@fai/friendli-suite/views/layout/header/items/suite-logo';
import { FriendliTextIcon } from '@fai/svgr';
import { TextVariants } from '@fai/ui/components/typography/variants';

interface AuthProps {
  rightItems?: (ButtonProps & { path: string; isOpenInNewTab?: boolean })[];
}

function MapHeaderAuthButtons({ rightItems }: Pick<AuthProps, 'rightItems'>) {
  return rightItems?.map(({ children, path, isOpenInNewTab }, index) => (
    <Button
      variant="outline"
      miw="unset"
      onClick={() => window.open(path, isOpenInNewTab ? '' : '_self')}
      key={`header-item-${randomInt(index, 100_000)}`}
    >
      {children}
    </Button>
  ));
}

function MapDropDownItems({ rightItems }: Pick<AuthProps, 'rightItems'>) {
  const theme = useMantineTheme();

  return rightItems?.map(({ children, path, isOpenInNewTab }, index) => (
    <Link
      key={`header-item-${randomInt(index, 100_000)}`}
      to={path}
      {...(isOpenInNewTab === true && {
        target: '_blank',
        rel: 'noreferrer',
      })}
      style={{ textDecoration: 'none' }}
    >
      <Menu.Item px={theme.pf.layout.xs} py={theme.pf.layout['2xs']}>
        <Text td="none" variant={TextVariants.TitleSm}>
          {children}
        </Text>
      </Menu.Item>
    </Link>
  ));
}

function AuthLayout({ rightItems }: AuthProps) {
  const theme = useMantineTheme();

  const [opened, { toggle }] = useDisclosure();
  const isItemMultiple = !isUndefined(rightItems) && rightItems?.length > 1;

  return (
    <AppShell header={{ height: rem(64) }}>
      <AppShell.Header
        bg={theme.pf.color.fg.white}
        style={{
          borderBottom: `${rem(1)} solid ${theme.pf.color.border.neutral.default}`,
        }}
      >
        <Flex align="center" h="100%" justify="space-between" px={theme.pf.layout.sm}>
          <Group gap={theme.pf.layout['2xs']} wrap="nowrap">
            <SuiteLogo path={Paths.login.default} />
            <FriendliTextIcon height={14} width={60} />
          </Group>

          {/* Mobile view */}
          <Box hiddenFrom="lg">
            {isItemMultiple === true ? (
              <Menu
                offset={{
                  mainAxis: 14,
                  crossAxis: 0,
                  // -34 matches vertical theme.pf.layout.sm
                  // crossAxis: -34,
                  alignmentAxis: null,
                }}
                position="bottom"
                shadow="md"
                onChange={toggle}
              >
                <Menu.Target>
                  <Burger opened={opened} />
                </Menu.Target>
                <Menu.Dropdown>{MapDropDownItems({ rightItems })}</Menu.Dropdown>
              </Menu>
            ) : (
              <Group>{MapHeaderAuthButtons({ rightItems })}</Group>
            )}
          </Box>

          {/* Web view */}
          <Group visibleFrom="lg">{MapHeaderAuthButtons({ rightItems })}</Group>
        </Flex>
      </AppShell.Header>

      <AppShell.Main>
        <Suspense>
          <Outlet />
        </Suspense>
      </AppShell.Main>
    </AppShell>
  );
}

export default AuthLayout;
